// Playback helpers for the video-like map experience.
//
// The main experience is an automatic day-by-day replay of the /api/days
// sequence. This module centralizes the pure helpers so playback logic
// can be unit-tested without React or the DOM.
//
// Rules:
//   - Playback starts at index 0 (days[0], the earliest date).
//   - Each tick advances to the next index.
//   - When the last index is reached, playback stops on the last date.
//     The caller can offer a restart action.
//   - Progress is expressed as a 0..1 fraction across the date sequence.

import type { ISODate } from './types';

/**
 * Base playback interval per day, in milliseconds, at 1× speed.
 *
 * v12: lowered from 750 ms to 500 ms (1.5× faster). With 85 playable
 * days the base run is now ~42.5 s (was ~64 s). A 2× speed control
 * halves this again to ~21 s.
 */
export const PLAYBACK_INTERVAL_BASE_MS = 500;

/**
 * The discrete speed multipliers exposed to the UI. The playback tick
 * uses `PLAYBACK_INTERVAL_BASE_MS / speed` as its delay, so a larger
 * multiplier ⇒ shorter interval ⇒ faster replay.
 */
export const PLAYBACK_SPEEDS = [1, 2] as const;
export type PlaybackSpeed = typeof PLAYBACK_SPEEDS[number];
export const PLAYBACK_SPEED_DEFAULT: PlaybackSpeed = 1;

/** Effective per-day tick interval for a given speed multiplier. */
export function intervalForSpeed(speed: PlaybackSpeed): number {
  return Math.round(PLAYBACK_INTERVAL_BASE_MS / speed);
}

/** Cycle to the next speed in PLAYBACK_SPEEDS, wrapping at the end. */
export function nextSpeed(speed: PlaybackSpeed): PlaybackSpeed {
  const i = PLAYBACK_SPEEDS.indexOf(speed);
  const next = PLAYBACK_SPEEDS[(i + 1) % PLAYBACK_SPEEDS.length];
  return next;
}

/**
 * Legacy export for any external caller that was reading the constant
 * directly. Equals `intervalForSpeed(PLAYBACK_SPEED_DEFAULT)` i.e. the
 * default-speed tick interval. Prefer `intervalForSpeed(speed)` at new
 * call sites.
 */
export const PLAYBACK_INTERVAL_MS = intervalForSpeed(PLAYBACK_SPEED_DEFAULT);

export type PlaybackStatus = 'idle' | 'playing' | 'paused' | 'complete';

/** Clamp an integer into the valid range for the given day list. */
export function clampIndex(index: number, total: number): number {
  if (total <= 0) return 0;
  if (index < 0) return 0;
  if (index >= total) return total - 1;
  return Math.floor(index);
}

/** Next index with stop-at-end semantics. Returns null when already at end. */
export function nextIndex(index: number, total: number): number | null {
  if (total <= 0) return null;
  if (index >= total - 1) return null;
  return index + 1;
}

/** Fraction 0..1 across the timeline. Zero-based on index, one-based on denominator. */
export function progressFraction(index: number, total: number): number {
  if (total <= 1) return total === 1 ? 1 : 0;
  return Math.max(0, Math.min(1, index / (total - 1)));
}

/**
 * Human-readable short form of an ISO date, for example "Jan 3".
 * Uses UTC so we do not drift by one day on negative timezones.
 */
export function formatShortDate(iso: ISODate): string {
  // Expected format YYYY-MM-DD.
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const year = Number(m[1]);
  const month = Number(m[2]) - 1;
  const day = Number(m[3]);
  const d = new Date(Date.UTC(year, month, day));
  if (Number.isNaN(d.getTime())) return iso;
  const monthName = d.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
  return `${monthName} ${day}`;
}

/** Small label like "Jan 3 to Apr 1" describing the full sequence range. */
export function formatRangeLabel(days: ISODate[]): string {
  if (days.length === 0) return '';
  if (days.length === 1) return formatShortDate(days[0]);
  return `${formatShortDate(days[0])} to ${formatShortDate(days[days.length - 1])}`;
}
